import {
  Component,
  ContentChild,
  Input,
  OnInit,
  OnChanges,
  SimpleChanges,
  TemplateRef,
  inject,
  ChangeDetectionStrategy,
  DestroyRef,
  ChangeDetectorRef
} from '@angular/core';
import { AnalyticsService } from '@shared/services/api/analytics.service';
import { AnalyticsExportService } from '@shared/services/api/analytics-export.service';
import { DataColumn } from '@shared/entities/analytics/dataColumn';
import { DataRow } from '@shared/entities/analytics/dataRow';
import { Subject } from 'rxjs';
import { debounceTime, finalize, switchMap, catchError, tap } from 'rxjs/operators';
import { of } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

interface AnalyticsResult {
  rows: DataRow[];
  columns?: DataColumn[];
  totalRows?: number;
}

interface PaginationState {
  page: number;
  size: number;
}

@Component({
  selector: 'app-analytics-widget',
  templateUrl: './analytics-widget.component.html',
  styleUrls: ['./analytics-widget.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AnalyticsWidgetComponent implements OnInit, OnChanges {
  @ContentChild(TemplateRef) contentTemplate!: TemplateRef<any>;
  @Input() query = 'count-trays';
  @Input() filters?: Record<string, any[]> | null = undefined;
  @Input() sortBy?: object | null = undefined;
  @Input() centered?: boolean = false;

  @Input() sortable = false;
  @Input() paginable = false;
  @Input() pageSize = 5;
  @Input() exportable = false;
  @Input() hideNoData = false;

  result: AnalyticsResult = { rows: [] };
  totalRows = 0;
  pagination: PaginationState = { page: 1, size: 5 };
  loading = false;
  hasError = false;

  private readonly analyticsService = inject(AnalyticsService);
  private readonly exportService = inject(AnalyticsExportService);
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _cdr = inject(ChangeDetectorRef);
  private filterChanges$ = new Subject<void>();

  get context() {
    return {
      $implicit: this.result,
      totalPages: this.totalPages(),
      currentPage: this.pagination.page,
      pageSize: this.pagination.size,
      onPageChange: (page: number) => this.onPageChange(page),
      onSizeChange: (size: number) => this.onSizeChange(size)
    };
  }

  ngOnInit(): void {
    this.pagination.size = this.pageSize;

    this.filterChanges$
      .pipe(
        debounceTime(300),
        tap(() => {
          this.loading = true;
          this.hasError = false;
          this._cdr.markForCheck();
        }),
        switchMap(() => {
          if (!this.query) return of(null);

          return this.analyticsService
            .getByQuery(this.query, this.filters ?? null, this.sortBy, this.paginable ? this.pagination : undefined)
            .pipe(
              catchError((err: any) => {
                console.error('Error fetching analytics data', err);
                this.hasError = true;
                this._cdr.markForCheck();
                return of(null);
              })
            );
        }),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe({
        next: (data: any) => {
          if (data) {
            this.result = data || { rows: [] };
            this.totalRows = data?.totalRows ?? 0;
            this.hasError = false;
          } else {
            this.result = { rows: [] };
          }
          this.loading = false;
          this._cdr.markForCheck();
        }
      });

    this.filterChanges$.next();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['filters'] || changes['query'] || changes['sortBy']) {
      if (changes['filters'] || changes['query']) {
        this.pagination.page = 1;
      }
      this.filterChanges$.next();
    }
  }

  fetchData(): void {
    this.filterChanges$.next();
  }

  totalPages(): number {
    return this.totalRows && this.pagination.size ? Math.ceil(this.totalRows / this.pagination.size) : 1;
  }

  onPageChange(page: number): void {
    this.pagination.page = page;
    this.fetchData();
  }

  onSizeChange(size: number): void {
    this.pagination.size = size;
    this.pagination.page = 1;
    this.fetchData();
  }

  onExportFormatSelected(format: 'csv' | 'pdf'): void {
    if (format === 'csv') {
      this.exportAsCSV();
    } else if (format === 'pdf') {
      this.exportAsPDF();
    }
  }

  exportAsCSV(): void {
    if (!this.query) return;

    this.loading = true;
    this._cdr.markForCheck();

    this.exportService
      .export(this.query, this.filters ?? null, 'csv')
      .pipe(
        finalize(() => {
          this.loading = false;
          this._cdr.markForCheck();
        }),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe({
        error: (err: any) => {
          console.error('Error exporting CSV', err);
        }
      });
  }

  exportAsPDF(): void {
    if (!this.query) return;

    this.loading = true;
    this._cdr.markForCheck();

    this.exportService
      .export(this.query, this.filters ?? null, 'pdf')
      .pipe(
        finalize(() => {
          this.loading = false;
          this._cdr.markForCheck();
        }),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe({
        error: (err: any) => {
          console.error('Error exporting PDF', err);
        }
      });
  }
}
